import { decode } from "./decoders/registry.ts";
import { isDynamicSource, authorizeDynamicImage } from "../network/SourceRegistry.ts";
import { resolveUrl } from "./UrlResolver.ts";
import {
  ExtractionError,
  type FieldRule,
  type ExtractionRule,
  type RecordValue,
  type SourceProfile,
  type Value,
} from "./types.ts";

export function transformValue(
  value: Value,
  rule: FieldRule,
  finalUrl: string,
  profile: SourceProfile,
): Value {
  for (const transform of rule.transforms ?? []) {
    if (typeof value !== "string" && transform !== "number")
      throw new ExtractionError("INVALID_RESPONSE", "Transformación requiere texto.");
    if (transform === "trim") value = String(value).trim();
    if (transform === "srcset") value = String(value).split(",")[0].trim().split(/\s+/)[0];
    if (transform === "absolute-url")
      value =
        rule.asset && isDynamicSource(profile.id)
          ? authorizeDynamicImage(profile.id, new URL(String(value).trim(), finalUrl).href).href
          : resolveUrl(String(value), finalUrl, profile);
    if (transform === "number") {
      if (
        (typeof value !== "string" && typeof value !== "number") ||
        (typeof value === "string" && !value.trim())
      )
        throw new ExtractionError("INVALID_RESPONSE", "Número vacío o no escalar.");
      value = Number(typeof value === "string" ? value.trim() : value);
      if (!Number.isFinite(value))
        throw new ExtractionError("INVALID_RESPONSE", "Número inválido.");
    }
  }
  return rule.decoder ? decode(rule.decoder, String(value)) : value;
}
function select(root: ParentNode, selector: string): Element | null {
  try {
    return root.querySelector(selector);
  } catch {
    throw new ExtractionError("SELECTOR_MISMATCH", `Selector inválido: ${selector}`);
  }
}
export function htmlField(
  root: ParentNode,
  rule: FieldRule,
  finalUrl: string,
  profile: SourceProfile,
): Value {
  if (rule.label) {
    if (!rule.selector)
      throw new ExtractionError("INVALID_PROFILE", "label requiere selector HTML.");
    let label: Element | undefined;
    try {
      label = [...root.querySelectorAll(rule.selector)].find(
        (node) => node.textContent?.trim().toLowerCase() === rule.label!.toLowerCase(),
      );
    } catch {
      throw new ExtractionError("SELECTOR_MISMATCH", `Selector inválido: ${rule.selector}`);
    }
    const value = label?.nextElementSibling;
    if (!value) {
      if (rule.required === false) return rule.all ? [] : null;
      throw new ExtractionError("SELECTOR_MISMATCH", `Etiqueta ausente: ${rule.label}`);
    }
    return htmlField(
      value,
      { ...rule, label: undefined, selector: rule.valueSelector, valueSelector: undefined },
      finalUrl,
      profile,
    );
  }
  if (rule.all) {
    if (!rule.selector) throw new ExtractionError("INVALID_PROFILE", "all requiere selector HTML.");
    let nodes: Element[];
    try {
      nodes = Array.from(root.querySelectorAll(rule.selector));
    } catch {
      throw new ExtractionError("SELECTOR_MISMATCH", `Selector inválido: ${rule.selector}`);
    }
    if (!nodes.length && rule.required !== false)
      throw new ExtractionError("SELECTOR_MISMATCH", `Campo ausente: ${rule.selector}`);
    return nodes.map((node) =>
      htmlField(node, { ...rule, all: false, selector: undefined }, finalUrl, profile),
    );
  }
  const node = rule.selector ? select(root, rule.selector) : (root as Element);
  let value: string | undefined;
  if (node) {
    if (rule.attributes) {
      for (const attribute of rule.attributes) {
        const candidate = (node as Element).getAttribute(attribute)?.trim();
        if (candidate) {
          value =
            attribute === "srcset" ? candidate.split(",")[0].trim().split(/\s+/)[0] : candidate;
          break;
        }
      }
    } else value = node.textContent ?? "";
  }
  if (value === undefined || (rule.required !== false && !value.trim())) {
    if (rule.required === false) return null;
    throw new ExtractionError(
      "SELECTOR_MISMATCH",
      `Campo ausente: ${rule.selector ?? "(elemento actual)"}`,
    );
  }
  return transformValue(value, rule, finalUrl, profile);
}
export function parseHtml(html: string): Document {
  return new DOMParser().parseFromString(html, "text/html");
}
export function extractHtml(
  document: Document,
  rule: ExtractionRule,
  finalUrl: string,
  profile: SourceProfile,
): RecordValue[] {
  let roots: ParentNode[];
  try {
    roots = rule.selector ? Array.from(document.querySelectorAll(rule.selector)) : [document];
  } catch {
    throw new ExtractionError("SELECTOR_MISMATCH", `Selector inválido: ${rule.selector}`);
  }
  if (!roots.length && !(rule.emptySelector && select(document, rule.emptySelector)))
    throw new ExtractionError("SELECTOR_MISMATCH", `Sin coincidencias: ${rule.selector}`);
  return roots.map((root) =>
    Object.fromEntries(
      Object.entries(rule.fields).map(([key, field]) => [
        key,
        htmlField(root, field, finalUrl, profile),
      ]),
    ),
  );
}
